import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { WidgetCrownBadge } from '../../../features/widgets/components/JackpotWidget/JackpotWidgetArt';
import { useSelectCategory } from '../../../hooks/useSelectCategory';
import type { Category } from '../../../stores/categories/CategoriesStore';
import { useStores } from '../../../stores/useStores';
import { CoinWithValue } from '../../CoinWithValue/CoinWithValue';
import Title from '../Title';

import styles from './JackpotCategoryHeader.module.scss';

interface JackpotCategoryHeaderProps {
  categoryId: Category['masterGameSubCategoryId'];
  /**
   * The collection's OWN name, resolved by the caller the same way every other row resolves it.
   *
   * The row is not renamed: this header adds the pot to the collection an operator configured, it does
   * not relabel it. It used to print a fixed "Crown Jackpot" title, which hid whatever the category was
   * actually called and made two jackpot collections indistinguishable.
   */
  name: string;
}

/**
 * Lobby category header for the jackpot collection (FE_DESIGN_SPEC §7.1), frame `10083:10232`:
 * `<collection name>  ((crown)[coin] 487,154,146)            See All`
 *
 * The NAME is the collection's own, passed in by the caller — this header decorates a row with the pot,
 * it does not rename it.
 *
 * The amount sits in a rounded pill with the crown wheel badge hung off its left end — the same
 * badge art the two jackpot widgets ship, not the placeholder `<Crown />` glyph this used to draw.
 * The badge is decorative next to a figure that is already spoken, so its `alt` is empty; it comes
 * last in the DOM because CSS positions it, and reading the amount before the ornament is the right
 * order aloud.
 *
 * Renders nothing unless the jackpot is on screen for this player — `canShowCategoryRow`, the same gate
 * the widgets read, so a display window that hides the widget hides this header rather than leaving a
 * decorated row with no way into the jackpot. No skeleton, no empty row, no error state: an outage is
 * invisible in the lobby, and `Games` drops the whole row on the same condition.
 */
export const JackpotCategoryHeader = observer(({ categoryId, name }: JackpotCategoryHeaderProps) => {
  const { t } = useTranslation();
  const { coinTypeStore, jackpotStore } = useStores();
  const { selectCategory } = useSelectCategory();

  // The SUM across the ladder (§7.1), same figure every other jackpot surface shows.
  const { canShowCategoryRow, topLevelAmount, topLevelAmountLabel } = jackpotStore;

  // `canShowCategoryRow` already requires a figure; the explicit null check is what narrows the type.
  if (!canShowCategoryRow || topLevelAmount === null) {
    return null;
  }

  return (
    <div className={classNames('games__category-wrapper', styles.wrapper)}>
      <div className={styles.titleGroup}>
        <Title name={name} />
        <span className={styles.amount} data-testid="jackpot-category-amount">
          <CoinWithValue
            className={styles.coinRow}
            coinSide="left"
            coinType={coinTypeStore.coinType}
            // 15.22 against the frame's 16.1px text. The component's own default is 1em — 5% large.
            coinWidth="0.945em"
            dataTestId="jackpot-category-coin"
            valueClassName={styles.amountValue}
          >
            {topLevelAmountLabel}
          </CoinWithValue>
          <WidgetCrownBadge className={styles.badge} />
        </span>
      </div>
      <button
        className="games__see-all"
        data-testid="jackpot-category-see-all"
        onClick={() => selectCategory(categoryId)}
      >
        {t('common.seeAll')}
      </button>
    </div>
  );
});
